import { GiphyFetch } from '@giphy/js-fetch-api'
import { IGif } from '@giphy/js-types'
import { SearchContext } from '@giphy/react-components'
import React, { Reducer, useContext, useEffect, useReducer, useRef, useState } from 'react'
import { MARGIN } from '../constants'
import { AppContext, SearchMode } from '../context'
import { TRENDING } from './categories'
import { MadeByYou, TextGridHeader } from './categories/text-grid-sc'
import Overlay from './grid-overlay'
import { Grid, StyledScrollContainer } from './layout'
import NoResults from './no-results'
import { useGifPopover, EmojiPopover } from './gif-popover'

const columns: { [key in SearchMode]: number } = {
    gifs: 2,
    stickers: 3,
    emoji: 4,
    text: 2,
    videos: 2,
}

const limitLarge: { [key in SearchMode]: number } = {
    gifs: 15,
    stickers: 25,
    emoji: 25,
    text: 15,
    videos: 25,
}

const limitSmall: { [key in SearchMode]: number } = {
    gifs: 10,
    videos: 10,
    stickers: 20,
    emoji: 20,
    text: 10,
}

export let allGifs: IGif[] = []

type ResultsActionType = 'hasResults'
interface TextSearchAction {
    type: ResultsActionType
    searchTerm: string
    hasResults: boolean
}
interface TextSearchState {
    [key: string]: boolean
}
const reducer = (state: TextSearchState, action: TextSearchAction) => {
    switch (action.type) {
        case 'hasResults':
            if (state[action.searchTerm] === undefined) {
                return { ...state, [action.searchTerm]: action.hasResults }
            }
            return state
        default:
            return state
    }
}

const Stub = () => null

const TrendingGrid = () => {
    const {
        setIsFetching,
        onGifClick,
        config: { isBrowserExtension, width: appWidth },
        setDisplayGif,
        searchMode,
        apiKey,
    } = useContext(AppContext)

    const { fetchGifs, searchKey, term: searchTerm, activeChannel, fetchAnimatedText } = useContext(SearchContext)
    const [showAllTextResults, setShowAllTextResults] = useState(false)
    const [hasTextSearchResults, dispatch] = useReducer<Reducer<TextSearchState, TextSearchAction>>(reducer, {})
    const emojiPopover = useGifPopover()

    const giphyFetch = new GiphyFetch(apiKey)
    // let's avoid an immediate second fetch
    const limit = isBrowserExtension ? limitLarge[searchMode] : limitSmall[searchMode]

    const doubleTextGrid = searchTerm !== '' && searchTerm !== TRENDING && searchMode === 'text' && !showAllTextResults

    useEffect(() => {
        // keep an array of the current gifs for other componets to use for look up
        allGifs = []
        setShowAllTextResults(false)
    }, [searchMode])

    const fetchAndSave = async <T extends { data: IGif[] }>(f: Promise<T>): Promise<T> => {
        setIsFetching(true)
        const result = await f
        allGifs.push(...result.data)
        setIsFetching(false)
        return result
    }
    const trending = (offset: number) =>
        fetchAndSave(giphyFetch.trending({ offset, limit, type: searchMode as 'gifs' | 'stickers' | 'videos' }))
    const search = (offset: number) => fetchAndSave(fetchGifs(offset))
    const textSearch = async (offset: number) => {
        const result = await fetchAndSave(fetchGifs(offset))
        dispatch({ type: 'hasResults', searchTerm, hasResults: result.data.length > 0 })
        return result
    }
    const showGif = (gif: IGif) => setDisplayGif(allGifs.find((g: IGif) => gif.id === g.id)!)
    const width = appWidth - MARGIN * 2
    const isTrending = searchTerm === TRENDING || (searchTerm === '' && !activeChannel)
    let doFetch = isTrending ? trending : search
    if (searchMode === 'emoji') {
        doFetch = async (offset: number) => {
            let result = await fetchAndSave(giphyFetch.emojiDefaultVariations({ offset, limit }))
            if (searchTerm && searchTerm !== TRENDING) {
                result = { ...result }
                result.data = result.data.filter((gif) => gif.tags.some((tag) => tag.indexOf(searchTerm) !== -1))
            }
            return result
        }
    } else if (searchMode === 'text' && searchTerm !== '' && searchTerm !== TRENDING) {
        doFetch = (offset: number) => fetchAndSave(fetchAnimatedText(offset))
    }

    const hasTextResults = hasTextSearchResults[searchTerm]
    const key = `${searchKey}-${searchMode}-${hasTextResults}`
    const target = useRef<HTMLDivElement | null>(null)

    // will be applied non-gif grids to reduce the size
    // of the items
    const padding = searchMode === 'gifs' || searchMode === 'videos' ? 0 : 20
    // since there is padding, we adjust the width
    const newWidth = width - padding
    const style =
        doubleTextGrid && hasTextResults
            ? {
                  height: 175,
                  overflow: 'hidden',
              }
            : undefined

    const hideGridOverlay = searchMode === 'emoji' && emojiPopover.open

    const defaultGifClickHandler = isBrowserExtension ? showGif : onGifClick
    const emojiClickHandler: typeof onGifClick = (gif: IGif, ...rest) => {
        if (emojiPopover.open) {
            return
        }
        if (gif?.variation_count) {
            return emojiPopover.onGifClick(gif, ...rest)
        }
        return defaultGifClickHandler(gif, ...rest)
    }

    return (
        <StyledScrollContainer ref={target}>
            {target.current ? (
                <>
                    {doubleTextGrid && (
                        <MadeByYou onClick={() => setShowAllTextResults(true)} hasTextResults={!!hasTextResults} />
                    )}
                    <div style={style}>
                        <Grid
                            marginTop={searchMode === 'emoji' ? -MARGIN * 2 : 0}
                            padding={padding}
                            key={key}
                            fetchGifs={doFetch}
                            width={newWidth}
                            columns={columns[searchMode]}
                            onGifClick={searchMode === 'emoji' ? emojiClickHandler : defaultGifClickHandler}
                            gutter={4 + padding}
                            overlay={hideGridOverlay ? Stub : Overlay}
                            noLink
                            loaderConfig={{ root: target.current, rootMargin: `250px` }}
                            backgroundColor={searchMode !== 'gifs' ? `rgba(0,0,0,0)` : undefined}
                            noResultsMessage={<NoResults />}
                        />
                    </div>
                    {doubleTextGrid && <TextGridHeader isVisible={hasTextResults}>Search Results</TextGridHeader>}
                    {doubleTextGrid && (
                        <Grid
                            marginTop={0}
                            padding={padding}
                            key={`${key}-trending-text`}
                            fetchGifs={textSearch}
                            width={newWidth}
                            columns={columns[searchMode]}
                            onGifClick={defaultGifClickHandler}
                            gutter={4 + padding}
                            overlay={hideGridOverlay ? Stub : Overlay}
                            noLink
                            loaderConfig={{ root: target.current, rootMargin: `250px` }}
                            backgroundColor={`rgba(0,0,0,0)`}
                        />
                    )}
                </>
            ) : null}
            <EmojiPopover
                context={emojiPopover}
                fetchVariations={(id) => fetchAndSave(giphyFetch.emojiVariations(id))}
                gifWidth={Math.floor((newWidth - padding - 4) / columns[searchMode])}
                EmojiVariationsListProps={{
                    onGifClick: defaultGifClickHandler,
                    overlay: (props) => <Overlay {...props} isEmojiPopover={true} />,
                }}
            />
        </StyledScrollContainer>
    )
}

export default TrendingGrid
